"use client";

import { useEffect, useState, createContext, useContext, useRef } from "react";
import { isFirebaseInitialized } from "@/lib/firebase/config";
import { useToast } from "./ui/use-toast";

interface FirebaseContextType {
  isInitialized: boolean;
}

const FirebaseContext = createContext<FirebaseContextType>({
  isInitialized: false,
});

export function useFirebase() {
  return useContext(FirebaseContext);
}

export function FirebaseProvider({ children }: { children: React.ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const { toast } = useToast();
  const toastShown = useRef(false); // Track if we've shown the error toast

  useEffect(() => {
    const checkFirebase = () => {
      const initialized = isFirebaseInitialized();
      // Check Firebase initialization status

      if (!initialized) {
        // Firebase is not initialized, check environment variables

        // Show toast notification if we're in the browser
        if (typeof window !== "undefined") {
          toast({
            title: "אירעה שגיאה",
            description: "מערכת התגובות לא זמינה כרגע. בדיקת חיבור מחדש...",
            variant: "accent2",
            duration: 5000,
          });
        }
      } else {
        // Firebase is initialized successfully
      }

      setIsInitialized(initialized);
    };

    // FirebaseProvider mounted

    // Check initially
    checkFirebase();

    // Recheck every 5 seconds if not initialized
    const interval = setInterval(() => {
      if (!isInitialized) {
        // Retrying Firebase initialization
        checkFirebase();
      }
    }, 5000);

    return () => {
      clearInterval(interval);
      // FirebaseProvider unmounted
    };
  }, [toast, isInitialized]);

  return (
    <FirebaseContext.Provider value={{ isInitialized }}>
      {children}
    </FirebaseContext.Provider>
  );
}
